import type React from "react";
import { useEffect, useState } from "react";
import type { LoaderFunction } from "react-router-dom";
import { Link, useLoaderData } from "react-router-dom";
import { LinkifiedDescription } from "../components/LinkifiedDescriptions";
import MapComponent from "../components/MapComponent";
import { PerformanceCard } from "../components/PerformanceCard";
import { getCurrentDate, parsePerformanceTime } from "../lib/formatTime";
import { byStartTime } from "../lib/sorting";
import {
  getArtistById,
  getDayById,
  getPerformancesByStageId,
  getStageById,
  getStageColor,
  loadFestivalData,
} from "../services/dataService";
import type { Artist, Day, Performance, Stage } from "../types";
import { BasePage } from "./BasePage";

interface PerformanceWithDetails extends Performance {
  artist: Artist | undefined;
  day: Day | undefined;
  stage: Stage | undefined;
}

type StagePageData = Stage & {
  performances: PerformanceWithDetails[];
};

export const loadStagePageData: LoaderFunction<StagePageData> = async ({ params }) => {
  await loadFestivalData();
  const stageId = params.id;
  if (!stageId) {
    throw new Response("Stage ID is required", { status: 400 });
  }

  const stage = getStageById(stageId);
  if (!stage) {
    throw new Response("Stage not found", { status: 404 });
  }

  const performances = getPerformancesByStageId(stageId).map((performance) => ({
    ...performance,
    artist: getArtistById(performance.artistId),
    day: getDayById(performance.dayId),
  }));

  return { ...stage, performances };
};

export const StagePage: React.FC = () => {
  const stage = useLoaderData() as StagePageData & {
    performances: PerformanceWithDetails[];
  };

  const [offsetX, setOffsetX] = useState(stage.offsetX);
  const [offsetY, setOffsetY] = useState(stage.offsetY);
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    setOffsetX(stage.offsetX);
    setOffsetY(stage.offsetY);
  }, [stage.offsetX, stage.offsetY]);

  const stageColor = getStageColor(stage.id);

  const upcomingPerformances: PerformanceWithDetails[] = stage.performances
    .map((performance): PerformanceWithDetails => {
      const stageObj = getStageById(performance.stageId);
      const day = getDayById(performance.dayId);
      const artist = getArtistById(performance.artistId);

      return {
        ...performance,
        stage: stageObj,
        day,
        artist,
      };
    })
    .filter((performance) => {
      const now = getCurrentDate();
      const endTime = parsePerformanceTime(performance.endTime, performance.dayId);
      return endTime >= now;
    })
    .sort(byStartTime);

  return (
    <BasePage>
      <div className="space-y-6 pb-6">
        {/* Stage Header Banner */}
        <div className="relative rounded-2xl overflow-hidden border border-white/5 bg-[#222222]/65">
          <div
            className="h-44 bg-black/40 flex items-end justify-start relative z-10 animate-bg-scroll"
            style={{
              backgroundImage: `url(${stage.image})`,
              backgroundSize: "cover",
            }}
          >
            {/* Dark Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-0" />
            <div className="p-6 relative z-10">
              <span
                className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5"
                style={{ color: stageColor }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: stageColor }}
                />
                Helyszín
              </span>
              <h2 className="text-3xl font-extrabold font-['Unbounded'] text-white uppercase tracking-tight drop-shadow-[0_0_10px_rgba(255,255,255,0.15)] mt-0.5">
                {stage.name}
              </h2>
            </div>
          </div>
        </div>

        {/* Details List */}
        <div className="space-y-6">
          {/* Description */}
          <div className="bg-[#222222]/65 border border-white/5 p-5 rounded-2xl backdrop-blur-md">
            <h3 className="font-['Unbounded'] text-sm font-bold text-accent mb-3.5 uppercase tracking-wider">
              <span className="ellipse-highlight-before-2 ellipse-highlight-after-3">Leírás</span>
            </h3>
            {stage.description ? (
              <LinkifiedDescription
                description={stage.description}
                className="text-sm text-muted leading-relaxed whitespace-pre-line"
              />
            ) : (
              <p className="text-sm text-muted leading-relaxed">
                Ez a színpad részletes leírása. Itt megjelenhet a színpad helyszíne, kapacitása és
                egyéb fontos információk.
              </p>
            )}
          </div>

          {/* Map Position */}
          <div className="p-5 bg-[#222222]/65 border border-white/5 rounded-2xl backdrop-blur-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-['Unbounded'] text-sm font-bold text-white uppercase tracking-wider">
                Térkép Helyzet
              </h3>
              <button
                type="button"
                onClick={() => setDebugMode(!debugMode)}
                className={`hidden text-[11px] px-2.5 py-1 rounded-lg border transition-all font-semibold ${
                  debugMode
                    ? "bg-main/15 border-main/30 text-main"
                    : "bg-white/5 border-white/10 text-muted hover:text-white"
                }`}
              >
                {debugMode ? "Debug Ki" : "Koordináta Debug"}
              </button>
            </div>
            <div className="block border border-white/5 rounded-xl p-1 overflow-hidden bg-black/40 relative">
              {debugMode ? (
                <MapComponent
                  stage={{ ...stage, offsetX, offsetY }}
                  interactive
                  onCoordinatesChange={(x, y) => {
                    setOffsetX(x);
                    setOffsetY(y);
                  }}
                />
              ) : (
                <Link to="/map" className="block hover:border-[#5ecfbe]/40 transition-all">
                  <MapComponent stage={{ ...stage, offsetX, offsetY }} />
                </Link>
              )}
            </div>

            {debugMode && (
              <div className="mt-4 p-4 rounded-xl border border-white/10 bg-[#171226]/80 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#5ecfbe] uppercase tracking-wider">
                    Szerkesztő adatok
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setOffsetX(stage.offsetX);
                      setOffsetY(stage.offsetY);
                    }}
                    className="text-[11px] bg-white/5 border border-white/10 hover:bg-white/10 text-white px-2.5 py-1 rounded-md transition-all font-semibold"
                  >
                    Alaphelyzet (stages.json)
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="offset-x-input"
                      className="block text-[10px] text-[#9f98b5] uppercase font-bold mb-1"
                    >
                      OffsetX
                    </label>
                    <input
                      id="offset-x-input"
                      type="number"
                      value={offsetX}
                      onChange={(e) => setOffsetX(Number(e.target.value))}
                      className="w-full bg-black/45 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#5ecfbe]/60"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="offset-y-input"
                      className="block text-[10px] text-[#9f98b5] uppercase font-bold mb-1"
                    >
                      OffsetY
                    </label>
                    <input
                      id="offset-y-input"
                      type="number"
                      value={offsetY}
                      onChange={(e) => setOffsetY(Number(e.target.value))}
                      className="w-full bg-black/45 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#5ecfbe]/60"
                    />
                  </div>
                </div>

                <div className="p-3 bg-black/55 border border-white/5 rounded-lg">
                  <p className="text-[10px] text-[#9f98b5] font-semibold uppercase tracking-wider mb-1">
                    JSON másolandó érték:
                  </p>
                  <code className="text-xs font-mono text-[#5ecfbe] block break-all select-all">
                    "offsetX": {offsetX}, "offsetY": {offsetY}
                  </code>
                </div>

                <p className="text-[10px] text-[#9f98b5]/85 italic leading-relaxed">
                  Tipp: Vonszold a fenti térképet a helyzet beállításához! A fenti JSON kódot
                  bemásolhatod a programme/stages.json fájlba a megfelelő színpadhoz.
                </p>
              </div>
            )}
          </div>

          {/* Schedule */}
          {upcomingPerformances.length > 0 && (
            <div>
              <h3 className="font-['Unbounded'] text-base font-bold text-[#5ecfbe] mb-4 uppercase tracking-wider pl-1">
                Közelgő események
              </h3>
              <div className="space-y-3">
                {upcomingPerformances.slice(0, 5).map((performance) => (
                  <UpcomingPerformanceCard key={performance.id} performance={performance} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </BasePage>
  );
};

function UpcomingPerformanceCard({
  performance,
}: {
  performance: PerformanceWithDetails;
}) {
  return (
    <PerformanceCard
      performance={{
        ...performance,
        artist: performance.artist || undefined,
        stage: performance.stage || undefined,
        day: performance.day || undefined,
      }}
    />
  );
}

export default StagePage;
